import { activeCheckpointId } from './checkpoints';
import { entryAltitude, initialEnemies, placePlane, type LevelRun } from './level-runner';
import { resetBossFight } from './boss/boss-fight';
import { resetAttacks } from './boss/attacks';
import { resetDecoys } from './boss/decoys';
import { ARMOR_TIMER } from './lives';

/**
 * Respawn after a life lost with lives left (GDD §6.2, rules 4 and 5). Called by the DERRIBO >
 * REAPARICION transition of level-state.ts, which also starts the 2.0 s of invulnerability on the first
 * flap.
 * - At the last active checkpoint, or at the level start, with vy = 0 at the centre of the next gap.
 * - Armor and bribe are lost; score, dollars and lives are kept.
 * - Everything after the checkpoint line is regenerated from its data: pickups reappear, columns can
 *   score again and enemies go back to their initial position. Everything before it stays as it was.
 */
export function respawn(run: LevelRun): void {
  const { core, level } = run;
  const checkpointId = activeCheckpointId(run.checkpoints);
  const lineX = checkpointId ? run.checkpoints.checkpoints[run.checkpoints.activeIndex]!.x : 0;
  const after = (x: number) => (checkpointId ? x > lineX : true);

  level.pickups.forEach((pickup, index) => {
    if (after(pickup.x)) run.collectedPickups.delete(index);
  });
  level.columns.forEach((column, index) => {
    if (after(column.x)) run.passedColumns.delete(index);
  });
  const fresh = initialEnemies(level);
  run.enemies.forEach((enemy, index) => {
    if (after(level.enemies[index]!.x)) Object.assign(enemy, fresh[index]);
  });

  core.player = { x: lineX, y: entryAltitude(level, lineX), vy: 0 };
  // The boss comes back with full HP and starts its fight again (GDD §9.1, §6.2 rule 5).
  if (run.boss) resetBossFight(run.boss);
  if (run.decoys) resetDecoys(run.decoys);
  if (run.attacks) resetAttacks(run.attacks);
  core.armor = false;
  delete core.timers[ARMOR_TIMER];
  core.bribeRemaining = 0;
  placePlane(run, lineX);
}
